import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { interval, takeUntil, takeWhile } from 'rxjs';

export interface InactivityWarningDialogData {
  warningShownAt: number;
  logoutAfterWarningMs: number;
  warningAfterMs: number;
  onContinue: () => void;
}

@Component({
  selector: 'lib-inactivity-warning-dialog',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule],
  templateUrl: './inactivity-warning-dialog.component.html',
  styleUrl: './inactivity-warning-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InactivityWarningDialogComponent implements OnInit {
  private readonly data = inject<InactivityWarningDialogData>(MAT_DIALOG_DATA);
  private readonly destroyRef = inject(DestroyRef);

  readonly inactivityLabelMinutes = this.convertMsToMinutes(
    this.data.warningAfterMs,
  );
  readonly countdownLabel = signal('');
  readonly locked = signal(false);

  /** Starts a one-second interval to refresh the logout countdown label. */
  ngOnInit(): void {
    this.updateCountdown();
    interval(1000)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        takeWhile(() => this.countdownLabel() !== '0:00'),
      )
      .subscribe(() => {
        if (!this.locked()) {
          this.updateCountdown();
        } else {
          this.countdownLabel.set('0:00');
        }
      });
  }

  /** Locks the dialog; logout cannot be cancelled from this point. */
  lock(): void {
    this.locked.set(true);
  }

  /** Dismisses the warning and resets inactivity monitoring via the dialog callback. */
  onContinue(): void {
    if (this.locked()) {
      return;
    }
    this.data.onContinue();
  }

  /**
   * @param ms - Duration in milliseconds.
   * @returns Whole minutes for display in the inactivity message.
   */
  private convertMsToMinutes(ms: number): number {
    return Math.floor(ms / 60_000);
  }

  /** Recomputes remaining time until automatic logout and updates {@link countdownLabel}. */
  private updateCountdown(): void {
    const logoutAt = this.data.warningShownAt + this.data.logoutAfterWarningMs;
    const remainingMs = Math.max(0, logoutAt - Date.now());
    this.countdownLabel.set(this.formatRemaining(remainingMs));
  }

  /**
   * @param remainingMs - Milliseconds until logout.
   * @returns Countdown string in `m:ss` form.
   */
  private formatRemaining(remainingMs: number): string {
    const totalSeconds = Math.ceil(remainingMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}
